/* eslint-disable react/require-default-props */

import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useQuery } from 'react-query';
import debounce from 'debounce-promise';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { Message } from 'primereact/message';
import { InputText } from 'primereact/inputtext';
import { ProgressBar } from 'primereact/progressbar';
import { Checkbox } from 'primereact/checkbox';
import _intersection from 'lodash.intersection';
import { BasicProfile, getUsers } from '../../../common/graph';
import { getAllRoles } from '../../../services/acl';
import { FieldWrapper } from '../../Form';
import { isManagerRole, isOperationSustainability } from '../../../common/roles';

const Fields = styled.div`
  display: grid;
  grid-template-columns: minmax(0px, 1fr);
  gap: 16px;
  padding-top: 16px;
  padding-bottom: 16px;
`;

const CheckboxWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const debouncedGetUsers = debounce(getUsers, 200);

const filterByRoleName = (candidate: { label: string; value: string; data: any }, input: string) =>
  input ? candidate.data.name.toLowerCase().includes(input.toLowerCase()) : true;

export type Inputs = {
  user: BasicProfile;
  name: string;
  familyName: string;
  isAdmin: boolean;
  isActive: boolean;
  roles: any[];
};

const defaultValues = {
  user: undefined,
  name: undefined,
  familyName: undefined,
  isAdmin: false,
  isActive: true,
  roles: [],
};

type Props = {
  onSubmit?: SubmitHandler<Inputs>;
};

// eslint-disable-next-line
export const AddUserForm = React.forwardRef<HTMLFormElement, Props>(({ onSubmit = () => {} }, ref) => {
  const {
    setValue,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Inputs>({ mode: 'onChange', defaultValues });

  const { isError, isFetching, data: allRoles } = useQuery('allRoles', getAllRoles);

  const user = watch('user');

  useEffect(() => {
    if (!user) return;
    setValue('familyName', user.surname);
    setValue('name', user.givenName);
  }, [user, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} ref={ref}>
      {isFetching && <ProgressBar mode="indeterminate" style={{ height: '4px' }} />}
      <Fields>
        {isError && <Message severity="error" text="An error has occurred while loading data!" />}

        <FieldWrapper>
          <label htmlFor="user">User</label>
          <Controller
            name="user"
            control={control}
            rules={{ required: 'This is required field' }}
            render={({ field }) => (
              <AsyncSelect
                {...field}
                id="user"
                placeholder="Search name or email"
                getOptionLabel={(option) => `${option.mail}`}
                getOptionValue={(option) => option.id}
                cacheOptions
                defaultOptions
                loadOptions={(inputValue) => debouncedGetUsers(inputValue)}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: '6px',
                    padding: '4px',
                  }),
                  indicatorSeparator: () => ({}),
                }}
              />
            )}
          />
          {errors.user && <small className="p-error">{errors.user.message}</small>}
        </FieldWrapper>
        <FieldWrapper>
          <label htmlFor="name">Name</label>
          <Controller
            name="name"
            control={control}
            render={({ field }) => <InputText {...field} disabled style={{ width: '100%' }} />}
          />
        </FieldWrapper>
        <FieldWrapper>
          <label htmlFor="familyName">Family name</label>
          <Controller
            name="familyName"
            control={control}
            render={({ field }) => <InputText {...field} disabled style={{ width: '100%' }} />}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Controller
            name="isAdmin"
            defaultValue
            control={control}
            render={({ field: { onChange, value } }) => (
              <CheckboxWrapper>
                <Checkbox inputId="isAdmin" onChange={onChange} checked={value} />
                <label htmlFor="isAdmin">Is Admin</label>
              </CheckboxWrapper>
            )}
          />
        </FieldWrapper>
        <FieldWrapper>
          <label htmlFor="roles">Roles </label>
          <Controller
            name="roles"
            control={control}
            rules={{
              validate: (value) => {
                if (value.length > 1) {
                  const proccessedData = value.map((v) => ({
                    grant: v.grant.name,
                    places: v.places.map((p) => p.code),
                  }));
                  const managerLocations = proccessedData
                    .filter((p) => isManagerRole(p.grant))
                    .flatMap((p) => p.places);
                  const operationsSustLocations = proccessedData
                    .filter((p) => isOperationSustainability(p.grant))
                    .flatMap((p) => p.places);

                  if (_intersection(managerLocations, operationsSustLocations).length > 0) {
                    return 'It is not allowed to assign manager role (EE Manager, Mill Manager) and Operational Sustainability at the same time to the same location.';
                  }
                  return true;
                }
                return true;
              },
            }}
            render={({ field }) => (
              <Select
                {...field}
                placeholder="Search role name"
                options={allRoles}
                getOptionLabel={(option) => `${option.name}`}
                getOptionValue={(option) => option.name}
                filterOption={filterByRoleName}
                isMulti
                hideSelectedOptions
                menuPosition="fixed"
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: '6px',
                    padding: '4px',
                  }),
                  indicatorSeparator: () => ({}),
                }}
              />
            )}
          />
          {errors.roles && <small className="p-error">{errors.roles.message}</small>}
        </FieldWrapper>
      </Fields>
    </form>
  );
});
